import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/shared/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-[var(--r-sm)] text-[12.5px] font-semibold transition-[background,border-color,color,box-shadow,opacity] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none",
  {
    variants: {
      variant: {
        default:
          "bg-[var(--accent)] text-white shadow-[0_4px_14px_rgba(255,59,84,0.28)] border border-transparent hover:bg-[#ff5167]",
        outline:
          "border border-[var(--line)] bg-[var(--elev)] text-[var(--text)] hover:border-[var(--line-2)] hover:bg-[#282b35]",
        ghost: "border border-transparent bg-transparent text-[var(--text)] hover:bg-[var(--elev)]",
        on: "border border-[var(--accent-line)] bg-[var(--accent-soft)] text-[var(--accent)] hover:bg-[var(--accent-soft)]",
        danger:
          "border border-[rgba(255,80,100,0.3)] bg-[rgba(255,60,84,0.08)] text-[#ff7587] hover:bg-[rgba(255,60,84,0.16)]",
      },
      size: {
        default: "h-9 px-3 py-2",
        sm: "h-7 rounded-[var(--r-sm)] px-2.5 text-[11.5px]",
        lg: "h-10 rounded-[var(--r-sm)] px-6",
        icon: "h-[29px] w-[29px] rounded-[8px]",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
